/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable no-param-reassign,no-restricted-syntax */
import moment from "moment";
import { v4 as uuid } from "uuid";

import { TFunction } from "i18next";
import {
  ConditionRuleData,
  REPORTING_PRIORITY,
  PATIENT_CLASS,
  UntaggedConditionRuleData,
} from "@advapacs/common";
import {
  BETWEEN_DATE_AND_TIME_INTERVAL,
  BETWEEN_DATE_INTERVAL,
  BETWEEN_TIME_INTERVAL,
  DAY_OF_WEEK,
  DICOM,
  DYNAMIC,
  HL7,
  IN_RANGE,
  MODALITY,
  BODY_PART,
  GENDER,
  EXAM_CODE,
  AGE_RANGE,
  STUDY_DESCRIPTION,
  ORGANISATION,
  OUT_OF_RANGE,
  SCRIPT,
  SITE,
  TIME_OF_DAY,
} from "../../constant";

export const findElementById = (
  dataArr: ConditionRuleData[],
  id: string
): ConditionRuleData | null => {
  for (const item of dataArr) {
    if (item.id === id) {
      return item;
    }
    if (item.conditionRules.length > 0) {
      const rule = findElementById(item.conditionRules, id);
      if (rule) {
        return rule;
      }
    }
  }
  return null;
};

export const findRule = (
  id: string,
  conditionRule: ConditionRuleData
): ConditionRuleData | null => {
  if (conditionRule.id === id) {
    return conditionRule;
  }
  return findElementById(conditionRule.conditionRules, id);
};

export const stripTags = (rules: UntaggedConditionRuleData): void => {
  delete rules.id;
  delete rules.scope;
  for (const item of rules.conditions) {
    if (item) {
      delete item.id;
      delete item?.type;
      delete item.scope;
      delete item?.dicom?.annotations; // For DICOM
    }

    if (
      item?.dicom &&
      (item?.dicom?.operator === IN_RANGE ||
        item?.dicom?.operator === OUT_OF_RANGE)
    ) {
      // exceptional case
      item.dicom.value = `${item?.dicom?.from},${item?.dicom?.to}`;

      if (item) {
        delete item.dicom.from;
        delete item.dicom.to;
      }
    }

    if (
      item?.stringProperty &&
      (item?.stringProperty?.operator === IN_RANGE ||
        item?.stringProperty?.operator === OUT_OF_RANGE)
    ) {
      // exceptional case
      item.stringProperty.value = `${item?.stringProperty?.from},${item?.stringProperty?.to}`;

      if (item) {
        delete item.stringProperty.from;
        delete item.stringProperty.to;
      }
    }

    if (
      item?.dynamic &&
      (item?.dynamic?.operator === BETWEEN_DATE_INTERVAL ||
        item?.dynamic?.operator === BETWEEN_TIME_INTERVAL ||
        item?.dynamic?.operator === BETWEEN_DATE_AND_TIME_INTERVAL)
    ) {
      // exceptional case
      item.dynamic.value = `${item?.dynamic?.from},${item?.dynamic?.to}`;

      if (item) {
        delete item.dynamic.from;
        delete item.dynamic.to;
      }
    }
  }
  for (const item of rules.conditionRules) {
    stripTags(item);
  }
};

export const existScriptInConditionRule = (
  conditionBuilderRule: UntaggedConditionRuleData
): boolean => {
  const existScriptInConditions = conditionBuilderRule.conditions.find(
    (x) => x.type === SCRIPT
  );
  if (existScriptInConditions) {
    return true;
  }

  const existScriptInConditionRules = conditionBuilderRule.conditionRules.find(
    (x) => existScriptInConditionRule(x)
  );
  return !!existScriptInConditionRules;
};
export const addTags = (rules: UntaggedConditionRuleData): void => {
  rules.id = uuid();
  rules.scope = "rules";
  for (const item of rules.conditions) {
    item.id = uuid();
    item.scope = "conditions";
    if (item?.timeOfDay) {
      item.type = TIME_OF_DAY;
    }
    if (item?.dayOfWeek) {
      item.type = DAY_OF_WEEK;
    }
    if (item?.dicom) {
      item.type = DICOM;

      if (
        item?.dicom?.operator === IN_RANGE ||
        item?.dicom?.operator === OUT_OF_RANGE
      ) {
        // exceptional case
        const range = item?.dicom?.value?.split(",");
        delete item.dicom.value;
        if (range && range.length === 2) {
          [item.dicom.from] = range;
          [, item.dicom.to] = range;
        }
      }
    }
    if (item?.hl7) {
      item.type = HL7;
    }

    if (item?.stringProperty) {
      const property = item?.stringProperty?.property;
      switch (property) {
        case "studyDescription":
          item.type = STUDY_DESCRIPTION;
          break;
      }

      if (
        item?.stringProperty?.operator === IN_RANGE ||
        item?.stringProperty?.operator === OUT_OF_RANGE
      ) {
        // exceptional case
        const range = item?.stringProperty?.value?.split(",");
        delete item.stringProperty.value;
        if (range && range.length === 2) {
          [item.stringProperty.from] = range;
          [, item.stringProperty.to] = range;
        }
      }
    }

    if (item?.stringCollectionProperty) {
      const property = item?.stringCollectionProperty?.property;
      switch (property) {
        case "organisationId":
          item.type = ORGANISATION;
          break;
        case "siteId":
          item.type = SITE;
          break;
        case "modality":
          item.type = MODALITY;
          break;
        case "patientClass":
          item.type = PATIENT_CLASS;
          break;
        case "reportingPriority":
          item.type = REPORTING_PRIORITY;
          break;
        case "bodyPart":
          item.type = BODY_PART;
          break;
        case "examCode":
          item.type = EXAM_CODE;
          break;
        case "gender":
          item.type = GENDER;
          break;
      }
    } else if (item?.integerRangeProperty) {
      const property = item?.integerRangeProperty?.property;
      switch (property) {
        case "ageRange":
          item.type = AGE_RANGE;
          break;
      }
    }

    if (item?.script) {
      item.type = SCRIPT;
    }
    if (item?.dynamic) {
      item.type = DYNAMIC;
      if (
        item?.dynamic?.operator === BETWEEN_DATE_INTERVAL ||
        item?.dynamic?.operator === BETWEEN_TIME_INTERVAL ||
        item?.dynamic?.operator === BETWEEN_DATE_AND_TIME_INTERVAL
      ) {
        // exceptional case
        const range = item?.dynamic?.value?.split(",");
        delete item.dynamic.value;
        if (range && range.length === 2) {
          [item.dynamic.from] = range;
          [, item.dynamic.to] = range;
        }
      }
    }
  }
  for (const item of rules.conditionRules) {
    addTags(item);
  }
};

export const getWeekDays = (t: TFunction) => {
  const days = moment.weekdays();
  if (days[0] === "Sunday") {
    days.shift();
    days.push("Sunday");
  }
  const daysForSelect = days.map((day) => ({
    label: `${t(`${day.toLowerCase()}`)}`,
    value: day.toUpperCase(),
    isDisabled: false,
  }));
  return daysForSelect;
};

export const camelToSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
